import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { gql, useQuery } from "@apollo/client";
import { formatSimpleAddress } from "common/formatters/simple-address";
import {
  FiCreditCard,
  FiHome,
  FiMessageSquare,
  FiStar,
  FiChevronDown,
  FiX,
  FiCheck,
  FiChevronLeft,
  FiVideo,
  FiAward,
  FiImage,
  FiCalendar,
} from "react-icons/fi";
import {
  Avatar,
  Footer,
  MainHeader,
  MainWrapper,
  PageFullWrapper,
  Booking,
} from "components/front";
import {
  CommonServerSideProps,
  getCommonServerSideProps,
} from "api-common/common-server-side-props";
import { SessionProvider } from "common/session/session-context";
import { AddressDto } from "entities/address.dto";
import { AddToFavoriteButton } from "components/add-to-favorite-button/add-to-favorite-button";
import { createGoogleMapsLink } from "common/maps/create-google-maps-link";
import { formatHours } from "common/formatters/format-hours";
import { Button } from "reactstrap";
import { useOnClickOutside } from "common/use-on-click-outside";
import { ServiceDto } from "entities/service.dto";
import { GalleryTab } from "components/tabs/gallery-tab";
import { OpinionsTab } from "components/tabs/opinions-tab";
import { AnswersTab } from "components/tabs/answers-tab";
import { CalendarSettingsDto } from "entities/calendar-settings.dto";
import { SendMessageButton } from "components/chat/send-message-button";
import { intervals } from "components/calendar-settings/single-calendar-day-settings";
import { hasPremiumBasedOnUserSession } from "common/premium/has-premium-based-on-expiration-date";
import { Price } from "components/front/price.component";
import Head from "next/head";
import { parseUriSlug } from "common/url/create-slugs";
import { RoutesEnum, createRoute } from "common/routes";

export const getServerSideProps = getCommonServerSideProps;

const QUERY = gql`
  query MyQuery($id: Int!) {
    specialists_by_pk(id: $id) {
      id
      gallery
      description
      avatar_url
      shown_name
      calendar_settings
      experience
      user {
        id
        payment_expiration_at
        specialist {
          id
          gallery
          description
          avatar_url
          shown_name
          profession {
            id
            name
          }
          secondary_professions_specialists {
            profession {
              id
              name
            }
          }
        }
      }
      secondary_professions_specialists {
        id
        profession {
          id
          name
          parent_id
        }
      }
      profession {
        id
        name
      }
      opinions {
        id
        rating
        content
        author_id
      }
      addressesses {
        id
        is_online
        building_no
        local_no
        city
        postal_code
        street
        latitude
        longitude
        calendar_settings
        place_id
        specialist_id
      }
      calendar_settings_by_specialist_id(
        where: { specialist_id: { _eq: $id } }
      ) {
        id
        calendar_settings
        addressess {
          id
          is_online
          building_no
          local_no
          city
          postal_code
          street
          latitude
          longitude
          place_id
          specialist_id
        }
      }
      services {
        id
        name
        price
        price_to
        price_negotiable
        addresses
        timespan
      }
      reservations {
        id
        reservation_place
        reservation_at
        address_id
        reservation_timespan
        event_id
        event {
          id
          address_id
          price
          addressess {
            building_no
            city
            id
            is_online
            latitude
            longitude
            local_no
            postal_code
            street
          }
          timespan
          capacity
          name
          event_at
          event_place
          events_users {
            user_id
          }
          events_users_aggregate {
            aggregate {
              count
            }
          }
        }
        addressess {
          id
          building_no
          local_no
          city
          postal_code
          street
          latitude
          longitude
        }
        is_accepted
        is_rejected
        service
      }
      user {
        email
        payment_expiration_at
      }
    }
    answers(where: { specialist_id: { _eq: $id } }) {
      specialist_id
      id
      content
      question {
        content
      }
    }
    opinions(where: { specialist_id: { _eq: $id } }) {
      author_id
      content
      id
      rating
      reservation {
        service
        event {
          name
        }
      }
      specialist {
        shown_name
      }
      specialist_id
      user {
        payment_expiration_at
        first_name
        last_name
      }
      opinion_responses {
        id
        content
      }
    }
    opinions_aggregate(where: { specialist_id: { _eq: $id } }) {
      aggregate {
        avg {
          rating
        }
        count
      }
    }
  }
`;

const routesForPath: Record<string, string> = {
  "undefined": "Adresy",
  adresy: "Adresy",
  cennik: "Cennik",
  opinie: "Opinie",
  qa: "Pytania",
  doswiadczenie: "Doświadczenie",
  galeria: "Galeria",
};

const mapRouteToPath = (tab: string) => {
  return Object.entries(routesForPath)
    .map(([key, value]: any) => {
      if (value === tab) {
        return key;
      }

      return undefined;
    })
    .filter((key) => {
      return !!key;
    })[0];
};

const Specialist: React.FC<CommonServerSideProps> = ({ userSession }) => {
  const tabs = [
    "Adresy",
    "Cennik",
    "Opinie",
    "Pytania",
    "Doświadczenie",
    "Galeria",
  ];
  const icons = [
    <FiHome />,
    <FiCreditCard />,
    <FiStar />,
    <FiMessageSquare />,
    <FiAward />,
    <FiImage />,
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [activeAddressDrop, setActiveAddressDrop] = useState(false);
  const [activeAddress, setActiveAddress] = useState(0);
  const [activeServiceDrop, setActiveServiceDrop] = useState(false);
  const [activeService, setActiveService] = useState(0);

  const router = useRouter();

  const slugPath = router.query?.slugPath || ["Page", "home"];
  // const routeTab = router.query?.tab || "";

  const [slug, tab] = slugPath;

  const id = parseUriSlug(slug);

  const { data, loading } = useQuery(QUERY, {
    variables: { id },
  });

  const addressesToShow = data
    ? [
        ...data?.specialists_by_pk.addressesses,
        ...data?.specialists_by_pk.calendar_settings_by_specialist_id.map(
          (item: CalendarSettingsDto) => item.addressess
        ),
      ]
    : [];

  useEffect(() => {
    setActiveTab(routesForPath[tab?.toString()] as any);
  }, [tab]);

  useEffect(() => {
    if (!!data) {
      const addressToSet = addressesToShow[0];

      const serviceToSet: ServiceDto = data.specialists_by_pk.services.find(
        (service: ServiceDto) =>
          (service.addresses as any[]).includes(addressToSet.id.toString())
      );

      setActiveAddress(addressToSet?.id);
      setActiveService(serviceToSet?.id);
    }
  }, [data]);

  const [backdrop, setBackdrop] = useState(false);

  const ref = useRef<any>();
  useOnClickOutside(ref, () => {
    setBackdrop(false);
  });

  const addressDropdownRef = useRef<any>();
  useOnClickOutside(addressDropdownRef, () => {
    setActiveAddressDrop(false);
  });

  const activeServiceRef = useRef<any>();
  useOnClickOutside(activeServiceRef, () => {
    setActiveServiceDrop(false);
  });

  const hasPremium = hasPremiumBasedOnUserSession(userSession);

  const keywords = [
    "Specjalista",
    data?.specialists_by_pk.profession.name,
    ...new Set(addressesToShow.map((item) => item.city)),
  ].join(", ");

  const description = `${data?.specialists_by_pk.profession.name} ${data?.specialists_by_pk.shown_name} na portalu znany.pl`;

  return (
    <>
      <div
        className=" fixed  w-full h-full"
        style={{
          zIndex: backdrop ? 9990 : 0,
          opacity: backdrop ? 1 : 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          transition: "opacity 0.2s linear",
        }}
      />
      <SessionProvider userSession={userSession}>
        <Head>
          <title>
            {data?.specialists_by_pk.shown_name}
            {" >"} {data?.specialists_by_pk.profession.name} - znany.pl
          </title>
          <meta name="keywords" content={keywords} />
          <meta name="description" content={description} />
        </Head>
        <MainWrapper>
          <MainHeader />
          <PageFullWrapper noPadding bg={` bg-gray-100`}>
            <div
              className={`mb-8 w lg:w-11/12 xl:w-10/12 mx-auto grid grid-cols-5 gap-4`}
            >
              <div className="col-span-5 md:col-span-3 mt-16 lg:mt-4 flex text-gray-600 pt-2 lg:pt-0 md:pl-3">
                <a
                  onClick={() => {
                    router.push(createRoute(RoutesEnum.Front_Specialists));
                  }}
                  className={`flex cursor-pointer border border-gray-400 px-4 py-2 ml-3 md:ml-0 rounded items-center bg-white`}
                >
                  <FiChevronLeft />
                  <span>Wróć</span>
                </a>
              </div>

              {data && !loading && (
                <>
                  <div className="col-span-5 lg:col-span-3 row-span-3 p-4 rounded-lg bg-white">
                    <div className="grid grid-cols-12 pt-2">
                      <div className="gap-2 col-span-12 md:col-span-10 lg:col-span-9">
                        <Avatar
                          colSpan={2}
                          secondaryProfessions={data.specialists_by_pk.secondary_professions_specialists.map(
                            (p: any) => p.profession
                          )}
                          name={data?.specialists_by_pk.shown_name}
                          profession={data?.specialists_by_pk.profession}
                          id={data?.specialists_by_pk.id}
                          avatar_url={data?.specialists_by_pk.avatar_url}
                          stars={data?.opinions_aggregate.aggregate.avg.rating}
                          opinions={data?.opinions_aggregate.aggregate.count}
                          paymentExpirationDate={
                            data.specialists_by_pk.user.payment_expiration_at
                          }
                        />
                      </div>
                      <div className=" col-span-2 lg:col-span-3 text-right hidden md:block">
                        <AddToFavoriteButton
                          specialistId={data?.specialists_by_pk.id}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row gap-2 w-full mt-4 text-gray-500 ">
                      <Button
                        onClick={() => setBackdrop(true)}
                        color="primary"
                        className="flex gap-2 items-center justify-center"
                      >
                        <FiCalendar />
                        <span>Umów wizytę</span>
                      </Button>
                      <div className="flex justify-between">
                        <SendMessageButton
                          outline={true}
                          user={data?.specialists_by_pk.user}
                        />
                        <div className="md:hidden">
                          <AddToFavoriteButton
                            specialistId={data?.specialists_by_pk.id}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="text-sm text-gray-500 mt-4 whitespace-pre-wrap">
                      {data?.specialists_by_pk.description}
                    </div>

                    <div
                      className={`flex flex-wrap border-b text-sm mb-3 gap-x-3 xl:gap-x-5 text-gray-500 my-10 justify-center md:justify-start`}
                    >
                      {tabs.map((tab, i) => (
                        <button
                          key={i}
                          className={`flex items-center justify-center gap-x-1 py-2.5 select-none font-bold  
                      ${
                        activeTab === tab
                          ? "border-b-2 border-blue-600 cursor-auto text-blue-600"
                          : "cursor-pointer"
                      }`}
                          onClick={() => {
                            const route = router.asPath;
                            
                            if (slugPath.length > 1) {
                                if ("undefined" == mapRouteToPath(tab)) {
                                  router.replace(
                                    route.replace(
                                      mapRouteToPath(activeTab),
                                      ""
                                    )
                                  );

                                  return;
                                }

                                router.replace(
                                  route.replace(
                                    mapRouteToPath(activeTab),
                                    mapRouteToPath(tab)
                                  )
                                );
                            } else if (slugPath.length == 1) {
                              router.push(
                                route.concat(`/${mapRouteToPath(tab)}`)
                              );
                            }
                          }}
                        >
                          <div className={`text-sm`}>{icons[i]}</div>
                          <span className="text-xs md:text-sm">{tab}</span>
                        </button>
                      ))}
                    </div>

                    {activeTab === "Doświadczenie" && (
                      <div className="text-sm text-gray-500 whitespace-pre-wrap">
                        {data?.specialists_by_pk.experience}
                      </div>
                    )}

                    {activeTab === "Galeria" && (
                      <GalleryTab gallery={data?.specialists_by_pk.gallery} />
                    )}

                    {activeTab === "Adresy" && (
                      <div className="text-sm text-gray-500">
                        {addressesToShow.map((el: any, i: Number) => {
                          return (
                            <div
                              className="py-1 flex justify-between md:justify-start gap-2"
                              key={`${i}`}
                            >
                              <div className="flex">
                                {!el.is_online ? <FiHome /> : <FiVideo />}
                                <span className="ml-2">
                                  {formatSimpleAddress(el)}{" "}
                                </span>
                              </div>
                              {!el.is_online && (
                                <span className="pl-3">
                                  {" "}
                                  <a
                                    className="text-blue-600 hover:text-blue-700"
                                    href={createGoogleMapsLink(el)}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Mapa
                                  </a>
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {activeTab === "Pytania" && (
                      <>{data && <AnswersTab answers={data.answers} />}</>
                    )}

                    {activeTab === "Opinie" && (
                      <>{data && <OpinionsTab opinions={data.opinions} />}</>
                    )}

                    {activeTab === "Cennik" && (
                      <div className="text-sm text-gray-500">
                        {data?.specialists_by_pk.services.map(
                          (el: ServiceDto, i: Number) => {
                            return (
                              <div
                                className="flex py-1 justify-between mb-2 md:mb-0 lg:mb-2 xl:mb-0"
                                key={`${i}`}
                              >
                                <div className="flex items-start col-span-5 md:col-span-3">
                                  <FiCheck className="mr-1 mt-1" />
                                  {el.name}
                                </div>
                                <div className="col-span-2 flex flex-col justify-between  ml-5">
                                  <Price
                                    priceTo={el.price_to}
                                    priceNegotiable={el.price_negotiable}
                                    price={el.price}
                                    hasDiscount={hasPremium}
                                  />
                                  <div className="text-right text-xs text-gray-400">
                                    {formatHours(el.timespan)}
                                  </div>
                                </div>
                                {/* <div className='text-right col-span-5 md:col-span-2'>
																	{el.addresses.map((address: any) => (
																		<div>
																			{formatSimpleAddress(
																				data.specialists_by_pk.addressesses.find(
																					(a: any) => a.id === parseInt(address)
																				)
																			)}
																		</div>
																	))}
																</div> */}
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}
                  </div>

                  <div
                    className="p-4 rounded-lg bg-white col-span-5 lg:col-span-2 text-gray-600"
                    style={{
                      zIndex: backdrop ? 9991 : "initial",
                    }}
                    ref={ref}
                  >
                    <h2 className="border-b text-lg text-center font-medium pt-1 pb-2 text-black">
                      Umów spotkanie
                    </h2>
                    <p className="text-sm  text-gray-500 py-3">Wybierz adres</p>

                    <div
                      onClick={() => setActiveAddressDrop(!activeAddressDrop)}
                      className={`relative w-full text-sm cursor-pointer`}
                    >
                      <div
                        className={`bg-white p-3.5 pr-12 rounded-lg w-full border border-gray-400 `}
                      >
                        {addressesToShow.find(
                          (a: AddressDto) => a.id === activeAddress
                        )
                          ? formatSimpleAddress(
                              addressesToShow.find(
                                (a: AddressDto) => a.id === activeAddress
                              )
                            )
                          : "Specjalista nie dodał jeszcze żadnego adresu"}
                      </div>
                      {/* dropdownicon */}
                      <div
                        style={{
                          transform: "scale(0.95)",
                          transformOrigin: "top",
                        }}
                        className={`absolute w-11 h-full p-2 m-px top-0 right-0 bg-white rounded-r-lg  flex items-center justify-center cursor-pointer`}
                      >
                        {activeAddressDrop ? <FiX /> : <FiChevronDown />}
                      </div>
                      {/* options*/}
                      {activeAddressDrop && addressesToShow.length > 0 && (
                        <ul
                          ref={addressDropdownRef}
                          style={{
                            maxHeight: "340px",
                          }}
                          className={`overflow-y-scroll customscroll border shadow w-full absolute text-left bg-white mt-1 rounded-lg flex flex-col right-0 cursor-pointer z-30`}
                        >
                          {addressesToShow.map((el: AddressDto, i: number) => {
                            return (
                              <li
                                key={`${i}`}
                                onClick={() => {
                                  setActiveAddress(el.id);
                                  setActiveAddressDrop(!activeAddressDrop);
                                  if (
                                    !(
                                      (
                                        data.specialists_by_pk
                                          .services as ServiceDto[]
                                      ).find(
                                        (service) =>
                                          service.id === activeService
                                      )?.addresses as string[]
                                    )?.includes(el.id.toString())
                                  ) {
                                    setActiveService(
                                      data.specialists_by_pk.services.find(
                                        (service: ServiceDto) =>
                                          (
                                            service.addresses as string[]
                                          ).includes(el.id.toString())
                                      )?.id
                                    );
                                  }
                                }}
                                className={` px-3 py-1 leading-8 border-b hover:bg-gray-100 w-full`}
                              >
                                {formatSimpleAddress(el)}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>

                    <p className="text-sm  text-gray-500 py-3">
                      Wybierz usługę
                    </p>

                    <div
                      onClick={() => setActiveServiceDrop(!activeServiceDrop)}
                      className={`relative w-full text-sm cursor-pointer`}
                    >
                      <div
                        className={`bg-white p-3.5 pr-12 rounded-lg w-full border border-gray-400 `}
                      >
                        {data?.specialists_by_pk.services?.find(
                          (a: ServiceDto) => a.id === activeService
                        )?.name ??
                          "Skontaktuj się ze specjalistą w celu umówienia wizyty"}
                      </div>
                      <div
                        style={{
                          transform: "scale(0.95)",
                          transformOrigin: "top",
                        }}
                        className={`absolute w-11 h-full p-2 m-px top-0 right-0 bg-white rounded-r-lg  flex items-center justify-center cursor-pointer`}
                      >
                        {activeServiceDrop ? <FiX /> : <FiChevronDown />}
                      </div>

                      {/* options*/}
                      {activeServiceDrop &&
                        data?.specialists_by_pk.services.filter(
                          (service: ServiceDto) =>
                            (service.addresses as string[]).includes(
                              activeAddress.toString()
                            )
                        ).length > 0 && (
                          <ul
                            ref={activeServiceRef}
                            style={{
                              maxHeight: "340px",
                            }}
                            className={`overflow-y-scroll customscroll border shadow w-full absolute text-left bg-white mt-1 rounded-lg flex flex-col z-50 right-0 cursor-pointer`}
                          >
                            {data?.specialists_by_pk.services
                              .filter((service: ServiceDto) =>
                                (service.addresses as any).includes(
                                  activeAddress.toString()
                                )
                              )
                              .map((el: ServiceDto, i: number) => {
                                return (
                                  <li
                                    key={`${i}`}
                                    onClick={() => {
                                      setActiveService(el.id);
                                      setActiveServiceDrop(!activeServiceDrop);
                                    }}
                                    className={` px-3 py-1 leading-8 border-b hover:bg-gray-100 w-full`}
                                  >
                                    {el.name} - {intervals[el.timespan]}
                                  </li>
                                );
                              })}
                          </ul>
                        )}
                    </div>
                  </div>

                  {addressesToShow.length > 0 && (
                    <div
                      className="rounded-lg bg-white p-4 col-span-5 lg:col-span-2 row-span-2"
                      style={{
                        zIndex: backdrop ? 9991 : "initial",
                      }}
                      ref={ref}
                    >
                      <p className="text-lg text-center font-medium pt-1">
                        {" "}
                        Wolne terminy{" "}
                      </p>
                      <p className="text-xs text-center">
                        {addressesToShow.find(
                          (address: AddressDto) => address.id === activeAddress
                        )
                          ? formatSimpleAddress(
                              addressesToShow.find(
                                (address: AddressDto) =>
                                  address.id === activeAddress
                              )
                            )
                          : null}
                      </p>
                      {data?.specialists_by_pk && (
                        <Booking
                          hideHeader
                          isBySpecialist={false}
                          isByPlace={!!userSession?.session?.place}
                          onTimeChoose={() => setBackdrop(false)}
                          calendar_settings_by_specialist_id={
                            data.specialists_by_pk
                              .calendar_settings_by_specialist_id
                          }
                          address={addressesToShow.find(
                            (address: AddressDto) =>
                              address.id === activeAddress
                          )}
                          service={data.specialists_by_pk.services.find(
                            (s: ServiceDto) => s.id === activeService
                          )}
                          reservations={data?.specialists_by_pk.reservations}
                          specialist={data?.specialists_by_pk}
                        />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </PageFullWrapper>
          <Footer />
        </MainWrapper>
      </SessionProvider>
    </>
  );
};
export default Specialist;
